import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { submitScore, hasUserWonRoulette, toKstIso, getGiftProducts } from '../utils/firestoreApi';
import { Timestamp } from 'firebase/firestore';
import { GAME_SETTINGS, getGameSettings, GAME_STATES } from '../config/gameConfig';

const FormContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 500px;
  color: white;
  display: flex;
  flex-direction: column;
  margin: 0px auto;
  overflow-y: auto;
`;

const FormTitle = styled.h1`
  text-align: center;
  font-size: 2rem;
  margin-bottom: 30px;
  color: white;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
  flex-shrink: 0;
  min-height: 0;
  height: auto;
`;

const FormCard = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 10px;
  backdrop-filter: blur(10px);
  box-shadow: rgba(0, 0, 0, 0.3) 0px 8px 32px;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 8px;
    max-width: 90vw;
    border-radius: 12px;
    min-height: auto;
  }
`;

const FormGroup = styled.div`
  flex-shrink: 0;
  min-height: 0;
  height: auto;
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  margin-bottom: 25px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  font-weight: bold;
  font-size: 1rem;
  color: white;
  flex-shrink: 0;
  min-height: 0;
  height: auto;
  min-width: 80px;
  white-space: nowrap;

  @media (max-width: 768px) {
    min-width: 65px;
    font-size: 0.85rem;
  }
`;

const ErrorMessage = styled.div`
  color: var(--secondary-color);
  font-size: 0.9rem;
  margin-top: 5px;
  min-height: 20px;
  flex-shrink: 0;
  min-height: 0;
  height: auto;
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-top: 8px;
  flex-shrink: 0;
  min-height: 0;
  height: auto;

  @media (max-width: 768px) {
    margin-top: 0px;
  }
`;

const Button = styled.button`
  margin-top: 10px;
  margin-bottom: 10px;
  padding: 10px 20px;
  border: none;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.$primary ? `
    background: var(--primary-gradient);
    color: white;
    box-shadow: 0 4px 15px rgba(255, 107, 107, 0.4);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(255, 107, 107, 0.6);
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }
  ` : `
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
      transform: translateY(-2px);
    }
  `}
  @media (max-width: 480px) {
    padding: 5px 16px;
    font-size: 0.9rem;
    border-radius: 18px;
    margin: 0px;
  }
`;

const ScoreDisplay = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 10px 6px;
  margin-bottom: 8px;
  text-align: center;
  flex-shrink: 0;
  min-height: 0;
  h3 {
    margin: 0 0 8px 0;
    color: #FFD700;
    font-size: 1.2rem;
  }
  .score-info {
    display: grid;
    grid-template-columns: ${
      props => {
        if (props.gameType === GAME_STATES.QUIZ_GAME) {
          return '1fr 1fr 1fr'; // 퀴즈: 3개 컬럼
        } else if (props.gameType === GAME_STATES.WORLDCUP_GAME || props.gameType === GAME_STATES.DRIP_BATTLE) {
          return '1fr'; // 월드컵/다이노: 1개 컬럼
        } else {
          return '1fr 1fr'; // 기본: 2개 컬럼 (리듬, 퍼즐)
        }
      }
    };
    gap: 8px;
  }
  .score-item {
    text-align: center;
  }
  .score-label {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 2px;
  }
  .score-value {
    font-size: 1rem;
    font-weight: bold;
    color: white;
  }

  @media (max-width: 768px) {
     h3 {
      font-size: 1rem;
    }
    .score-label {
      font-size: 0.85rem;
    }
    .score-value {
      font-size: 0.9rem;
    }
  }
`;

const ProductSelection = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 15px;
  margin-bottom: 15px;
  
  h3 {
    margin: 0 0 10px 0;
    color: #FFD700;
    font-size: 1rem;
    text-align: center;
  }
`;

const Select = styled.select`
  flex: 1;
  padding: 10px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background: rgba(255, 255, 255, 0.2);
    box-shadow: 0 0 15px rgba(255, 107, 107, 0.3);
  }
  
  option {
    background: #333;
    color: white;
    font-size: 0.85rem;
  }

  @media (max-width: 768px) {
    padding: 5px;
    font-size: 1rem;

    &::placeholder {
      font-size: 0.75rem;
    }
    
    option {
      font-size: 0.8rem;
    }
  }
`;

const Input = styled.input`
  flex: 1;
  box-sizing: border-box;
  padding: 10px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.9rem;
  }
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background: rgba(255, 255, 255, 0.2);
    box-shadow: 0 0 15px rgba(255, 107, 107, 0.3);
  }
  
  &:invalid {
    border-color: var(--primary-color);
  }

  @media (max-width: 768px) {
    padding: 5px;
    font-size: 1rem;
    border-radius: 8px;
    
    &::placeholder {
      font-size: 0.75rem;
    }
  }
`;

const PrivacyNotice = styled.div`
  font-size: 0.6rem;
  color:rgb(255, 255, 255);
  margin-bottom: 10px;
  word-break: keep-all;
`;

const PrivacyAgreement = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 15px;
  
  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    accent-color: var(--primary-color);
  }
  
  label {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;
  }
  
  a {
    font-size: 0.8rem;
    color: #FFD700;
    text-decoration: underline;
    margin-left: 6px;
  }

  @media (max-width: 768px) {
    input[type="checkbox"] {
      width: 12px;
      height: 12px;
      accent-color: var(--primary-color);
    }
    
    label {
      font-size: 0.7rem;
      color: rgba(255, 255, 255, 0.7);
      cursor: pointer;
    }
    
    a {
      font-size: 0.7rem;
      color: #FFD700;
      text-decoration: underline;
      margin-left: 6px;
    }
  }
`;

function GameResultForm({ gameResult, onSubmit, onRetry, gameType, userInfo }) {
  const [selectedProduct, setSelectedProduct] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [userData, setUserData] = useState({
    nickname: '',
    email: '',
    phone: '010'
  });

  // Firestore에서 상품 목록 가져오기
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoadingProducts(true);
        const productsData = await getGiftProducts();
        setProducts(productsData);
          } catch (error) {
      //console.error('상품 목록 로딩 실패:', error);
        // 기본 상품 목록으로 폴백
        setProducts([
          {
            id: 'nintendo',
            name: '닌텐도 스위치2(5명)',
          },
          {
            id: 'airpods max',
            name: '에어팟 맥스 2024(5명)',
          },
          {
            id: 'ipad mini',
            name: '아이패드 미니 A17 Pro(5명)',
          },
          {
            id: 'airpods pro',
            name: '에어팟 프로2(10명)',
          }
        ]);
      } finally {
        setLoadingProducts(false);
      }
    };

    loadProducts();
  }, []);

  const handleProductChange = (e) => {
    setSelectedProduct(e.target.value);
    // 상품 선택 시 에러 메시지 제거
    if (errors.products) {
      setErrors(prev => ({ ...prev, products: '' }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // 전화번호인 경우 숫자만 허용
    if (name === 'phone') {
      const numbersOnly = value.replace(/[^0-9]/g, '');
      setUserData(prev => ({
        ...prev,
        [name]: numbersOnly
      }));
    } else {
      setUserData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 유효성 검사
    const newErrors = {};
    
    if (!selectedProduct) {
      newErrors.products = '원하는 상품을 선택해주세요.';
    }
    
    if (!userData.nickname) {
      newErrors.nickname = '닉네임을 입력해주세요.';
    } else if (userData.nickname.length < 2 || userData.nickname.length > 20) {
      newErrors.nickname = '닉네임은 2~20자 사이로 입력해주세요.';
    }
    
    if (!userData.email) {
      newErrors.email = '이메일을 입력해주세요.';
    } else {
      // 이메일 유효성 검사 정규식
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(userData.email)) {
        newErrors.email = '올바른 이메일 형식을 입력해주세요.';
      }
    }
    
    if (!userData.phone) {
      newErrors.phone = '전화번호를 입력해주세요.';
    } else if (!userData.phone.startsWith('010')) {
      newErrors.phone = '전화번호는 010으로 시작해야 합니다.';
    } else if (userData.phone.length < 10 || userData.phone.length > 11) {
      newErrors.phone = '전화번호는 10-11자리로 입력해주세요.';
    }
    
    if (!privacyAgreed) {
      newErrors.privacy = '개인정보 수집 및 이용에 동의해주세요.';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // 게임 설정에서 컬렉션 정보 가져오기
      const gameConfig = getGameSettings(gameType);
      
      // 퍼즐게임의 경우 3x3과 4x4 구분하여 컬렉션 설정
      let gameCollection = gameConfig.resultCollection;
      if (gameType === GAME_STATES.PUZZLE_GAME && gameResult && gameResult.stage) {
        if (gameResult.stage === 1) {
          // 3x3 퍼즐
          gameCollection = gameConfig.puzzle3x3.resultCollection;
        } else if (gameResult.stage === 2) {
          // 4x4 퍼즐
          gameCollection = gameConfig.puzzle4x4.resultCollection;
        }
      }

      // 게임 타입별로 다른 데이터 구조로 저장
      let saveData = {
        nickname: userData.nickname,
        email: userData.email,
        phone: userData.phone,
        selectedProduct: selectedProduct, // 단일 상품 선택
        isAgreed: privacyAgreed,
        createdAt: toKstIso(Timestamp.now())
      };

      // 게임별 특정 데이터 추가 (설정에서 필드 정보 가져오기)
      let gameFields = gameConfig.fields;
      
      // 퍼즐게임의 경우 stage에 따라 다른 필드 설정
      if (gameType === GAME_STATES.PUZZLE_GAME && gameResult && gameResult.stage) {
        if (gameResult.stage === 1) {
          gameFields = gameConfig.puzzle3x3.fields;
        } else if (gameResult.stage === 2) {
          gameFields = gameConfig.puzzle4x4.fields;
        }
      }
      
      gameFields.forEach(field => {
        if (gameConfig.fieldMapping && gameConfig.fieldMapping[field]) {
          // 필드 매핑이 있는 경우 (리듬 게임 등)
          const possibleFields = gameConfig.fieldMapping[field];
          for (const possibleField of possibleFields) {
            if (gameResult && gameResult[possibleField] !== undefined) {
              saveData[field] = gameResult[possibleField];
              break;
            }
          }
        } else {
          // 일반적인 경우
          if (gameResult && gameResult[field] !== undefined) {
            saveData[field] = gameResult[field];
          }
        }
      });

      // Firebase에 게임 결과 저장
      await submitScore({
        ...saveData,
        gameCollection: gameCollection
      });

      // onSubmit 콜백에 데이터 전달
      // 룰렛에 정보 전달용
      const submissionData = {
        ...saveData,
        gameType: gameType,
        timestamp: toKstIso(Timestamp.now()),
        userPhone: userData.phone // 룰렛 당첨 여부 확인용
      };
      
      onSubmit(submissionData);

    } catch (error) {
      console.error('제출 중 오류:', error);
      setErrors({ submit: '점수 저장 중 오류가 발생했습니다. 다시 시도해주세요.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormContainer>
      <FormCard>
      {/* {gameType !== 'quiz' && ( */}
        <ScoreDisplay gameType={gameType}>
          <h3>게임 결과</h3>
          <div className="score-info">
            {gameType === 'rhythm' && (
              <>
                <div className="score-item">
                  <div className="score-label">최종 점수</div>
                  <div className="score-value">
                    {(gameResult?.totalScore || gameResult?.score || 0).toLocaleString()}
                  </div>
                </div>
                <div className="score-item">
                  <div className="score-label">최대 콤보</div>
                  <div className="score-value">
                    {gameResult?.totalCombo || gameResult?.combo || 0}
                  </div>
                </div>
              </>
            )}
            {gameType === GAME_STATES.PUZZLE_GAME && (
              <>
                <div className="score-item">
                  <div className="score-label">이동 횟수</div>
                  <div className="score-value">
                    {gameResult?.moves || 0}
                  </div>
                </div>
                <div className="score-item">
                  <div className="score-label">소요 시간</div>
                  <div className="score-value">
                    {gameResult?.timeSpent || '00:00:00.000'}
                  </div>
                </div>
              </>
            )}
            {/* {gameType === GAME_STATES.QUIZ_GAME && (
              <>
                <div className="score-item">
                  <div className="score-label">1라운드</div>
                  <div className="score-value">
                    {gameResult?.round1Answer || '-'}
                  </div>
                </div>
                <div className="score-item">
                  <div className="score-label">2라운드</div>
                  <div className="score-value">
                    {gameResult?.round2Answer || '-'}
                  </div>
                </div>
                   <div className="score-item">
                   <div className="score-label">3라운드</div>
                   <div className="score-value">
                     {gameResult?.round3Answer || '-'}
                   </div>
                 </div>
              </>
            )} */}
            {gameType === GAME_STATES.DRIP_BATTLE && (
              <>
                <div className="score-item">
                  <div className="score-label">최종 선택</div>
                  <div className="score-value">
                    {gameResult?.title || '-'}
                  </div>
                </div>
              </>
            )}
            {gameType === GAME_STATES.WORLDCUP_GAME && (
              <>
                <div className="score-item">
                  <div className="score-label">최종 선택</div>
                  <div className="score-value">
                    {gameResult?.title || '-'}
                  </div>
                </div>
              </>
            )}
            {gameType === GAME_STATES.DINO_GAME && (
              <>
                <div className="score-item">
                  <div className="score-label">최종 점수</div>
                  <div className="score-value">
                    {Math.floor(gameResult?.score) || '-'}
                  </div>
                </div>
                <div className="score-item">
                  <div className="score-label">소요 시간</div>
                  <div className="score-value">
                    {gameResult?.timeSpent || '00:00:00.000'}
                  </div>
                </div>
              </>
            )}
          </div>
        </ScoreDisplay>
        {/* )} */}
        
        <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', alignItems: 'stretch', minHeight: 0, height: 'auto', boxSizing: 'border-box', overflow: 'visible'}}>
        <ProductSelection>
          <FormGroup>
            <Label htmlFor="nickname">닉네임 *</Label>
            <Input
              type="text"
              id="nickname"
              name="nickname"
              value={userData.nickname}
              onChange={handleInputChange}
              placeholder="닉네임을 입력하세요"
              required
              maxLength={20}
            />
            {errors.nickname && <ErrorMessage>{errors.nickname}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="email">이메일 *</Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={userData.email}
              onChange={handleInputChange}
              placeholder="이메일을 입력하세요"
              required
              maxLength={50}
            />
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="phone">전화번호 *</Label>
            <Input
              type="tel"
              id="phone"
              name="phone"
              value={userData.phone}
              onChange={handleInputChange}
              placeholder="숫자만 입력 (예: 01012345678)"
              required
              maxLength={11}
            />
            {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="product">응모 상품 *</Label>
            {loadingProducts ? (
              <Select disabled style={{ opacity: 0.6 }}>
                <option>상품 목록을 불러오는 중...</option>
              </Select>
            ) : (
              <Select value={selectedProduct} onChange={handleProductChange}>
                <option value="">상품을 선택해주세요</option>
                {products.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </Select>
            )}
            {errors.products && <ErrorMessage>{errors.products}</ErrorMessage>}
          </FormGroup>
          </ProductSelection>
          <PrivacyNotice>
            ※ 개인정보는 경품 발송 및 응모 확인용으로만 사용됩니다.<br/>
            ※ 미성년자는 법정대리인의 동의가 필요하며, 연락 불가 또는 정보 불일치 시 당첨이 취소될 수 있습니다.
          </PrivacyNotice>

          <PrivacyAgreement>
            <input 
              type="checkbox" 
              id="privacy-agreement" 
              checked={privacyAgreed} 
              onChange={e => setPrivacyAgreed(e.target.checked)} 
            />
            <label htmlFor="privacy-agreement">
              개인정보 수집 및 이용 동의
            </label>
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer">전문 보기</a>
          </PrivacyAgreement>
          {errors.privacy && <ErrorMessage>{errors.privacy}</ErrorMessage>}

          <ButtonContainer>
            <Button type="button" onClick={onRetry}>
              다시 시도
            </Button>
            <Button type="submit" $primary disabled={isSubmitting || !privacyAgreed}>
              {isSubmitting ? '제출 중...' : '응모하기'}
            </Button>
          </ButtonContainer>

          <div style={{fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.7)', marginTop: '10px'}}>
            * 응모 후 룰렛을 통해 기프티콘 추첨을 확인해보세요!
          </div>
          {errors.submit && (
            <div style={{color: 'var(--primary-color)', fontSize: '0.9rem', marginTop: '10px', textAlign: 'center'}}>
              {errors.submit}
            </div>
          )}
        </form>
      </FormCard>
    </FormContainer>
  );
}

export default GameResultForm; 